/**
 * ticketOptions give:
 *  text - provided by freshdesk as a number status, so we translate it back to text on our side
 *  status - status options that we use for SoStatus component
 */

import { Status, StatusType } from "@/types/Status"

interface FreshdeskTicketOptions {
  freshdeskStatus: number
  text: string
  status: StatusType
}

export enum FreshdeskStatus {
  Open = 2,
  Pending = 3,
  Resolved = 4,
  Closed = 5,
  PendingWaitingForCustomer = 6,
  WaitingOnThirdParty = 7,
  PendingStillWaitingOnCustomer = 10,
  StillWaitingOnThirdParty = 11,
  PendingNETsInTraining = 12,
  ComplaintReceived = 13,
  WaitingOnAgent = 14,
  WaitingOnCustomer = 15,
  OmbudsmanComplaint = 16,
}

export const ticketOptions: FreshdeskTicketOptions[] = [
  {
    freshdeskStatus: FreshdeskStatus.Open,
    text: "Open",
    status: "caution",
  },
  {
    freshdeskStatus: FreshdeskStatus.Pending,
    text: "Pending",
    status: "caution",
  },
  {
    freshdeskStatus: FreshdeskStatus.Resolved,
    text: "Resolved",
    status: "normal",
  },
  {
    freshdeskStatus: FreshdeskStatus.Closed,
    text: "Closed",
    status: "default",
  },
  {
    freshdeskStatus: FreshdeskStatus.PendingWaitingForCustomer,
    text: "Pending - Waiting on Customer",
    status: "caution",
  },
  {
    freshdeskStatus: FreshdeskStatus.WaitingOnThirdParty,
    text: "Waiting on Third Party",
    status: "caution",
  },
  {
    freshdeskStatus: FreshdeskStatus.PendingStillWaitingOnCustomer,
    text: "Pending - Still Waiting on Customer",
    status: "caution",
  },
  {
    freshdeskStatus: FreshdeskStatus.StillWaitingOnThirdParty,
    text: "Still Waiting on Third Party",
    status: "caution",
  },
  {
    freshdeskStatus: FreshdeskStatus.PendingNETsInTraining,
    text: "Pending - NETs in training",
    status: "caution",
  },
  {
    freshdeskStatus: FreshdeskStatus.ComplaintReceived,
    text: "Complaint Received",
    status: "critical",
  },
  {
    freshdeskStatus: FreshdeskStatus.WaitingOnAgent,
    text: "Waiting on Agent",
    status: "caution",
  },
  {
    freshdeskStatus: FreshdeskStatus.WaitingOnCustomer,
    text: "Waiting on Customer",
    status: "caution",
  },
  {
    freshdeskStatus: FreshdeskStatus.OmbudsmanComplaint,
    text: "Ombudsman Complaint",
    status: "critical",
  },
]

export const ticketSource = [
  { freshdeskSource: 1, text: "Email", icon: "at-sign" },
  { freshdeskSource: 2, text: "Portal", icon: "monitor" },
  { freshdeskSource: 3, text: "Phone", icon: "phone" },
  { freshdeskSource: 4, text: "Forum", icon: "message" },
  { freshdeskSource: 5, text: "Twitter", icon: "twitter" },
  { freshdeskSource: 6, text: "Facebook", icon: "facebook" },
  { freshdeskSource: 7, text: "Chat", icon: "message" },
  { freshdeskSource: 8, text: "MobiHelp", icon: "message" },
  { freshdeskSource: 9, text: "Feedback Widget", icon: "clipboard-check" },
  { freshdeskSource: 10, text: "Outbound Email", icon: "at-sign" },
  { freshdeskSource: 11, text: "Ecommerce", icon: "at-sign" },
  { freshdeskSource: 12, text: "Bot", icon: "at-sign" },
  { freshdeskSource: 13, text: "Whatsapp", icon: "whatsapp" },
]

export const getStatus = (id: number): Status => {
  const ticket = ticketOptions.find((status) => status.freshdeskStatus === id)
  return new Status(ticket?.status || "default", ticket?.text || "Other")
}

export const sourceIcon = (id: number): string => {
  return (
    ticketSource.find((source) => source.freshdeskSource == id)?.icon ||
    "at-sign"
  )
}

export const sourceText = (id: number): string => {
  return (
    ticketSource.find((source) => source.freshdeskSource == id)?.text || "Other"
  )
}

/**
 * A snapshot mapping of Freshdesk group IDs to their display names.
 *
 * This is a static snapshot taken on April 23, 2025, preserving the group structure
 * that existed in Freshdesk at that time. Since we are transitioning away from
 * Freshdesk, this serves as a historical reference for legacy data processing
 * and ensures continuity during the migration period.
 *
 * @readonly
 */
export const legacyFreshdeskGroupNameMap: Record<number, string> = {
  7000022944: "Betr Support",
  7000022752: "Booking- External",
  7000023597: "CAB Correspondence",
  7000024343: "Capita",
  7000023837: "Collections",
  7000024328: "Collections Internal",
  7000005576: "Customer Service",
  7000016778: "Data Protection",
  7000023872: "DFS",
  7000022398: "ECO & GBIS",
  7000023279: "EI DNO Applications",
  7000022976: "EI Invoices",
  7000007065: "Emergency",
  7000022228: "Energy Independence",
  7000023358: "Exec Complaints",
  7000019959: "Facebook/Twitter Messages",
  7000023538: "FIT",
  7000023249: "Official Complaint Trackers",
  7000022174: "Operations Industry Contact",
  7000005574: "Payments",
  7000010749: "Resolutions",
  7000022040: "SEG",
  7000024013: "Specialist - Change of Tenancy",
  7000023316: "Specialist- Bill Accuracy",
  7000022306: "Specialist- Bill Generation",
  7000022307: "Specialist- Disputed Reads",
  7000022308: "Specialist- Erroneous Transfers",
  7000023205: "Specialist- Industry Corrections",
  7000024132: "Specialist- Meter Read Triage",
  7000022788: "Specialist- Metering",
  7000022309: "Specialist- Registrations",
  7000019979: "Specialist- Settlements",
  7000022557: "Test",
  7000023657: "Trustpilot Reviews",
  7000023698: "Typeform Bug or Incident Form Responses",

  // Staging groups
  62015000033666: "Anca-testing",
  1007000022752: "Booking- External",
  1007000023597: "CAB Correspondence",
  1007000023837: "Collections",
  1007000005576: "Customer Service",
  1007000016778: "Data Protection",
  1007000023872: "DFS",
  62015000033669: "Dorin_testing_2",
  62015000033670: "Dorin_testing_3",
  62015000033665: "Dorin-testing",
  1007000022944: "EC Support",
  1007000022398: "ECO & GBIS",
  1007000023279: "EI DNO Applications",
  1007000022976: "EI Invoices",
  1007000007065: "Emergency",
  1007000022228: "Energy Independence",
  1007000023358: "Exec Complaints",
  1007000019959: "Facebook/Twitter Messages",
  1007000023538: "FIT",
  1007000023249: "Official Complaint Trackers",
  1007000022174: "Operations Industry Contact",
  1007000005574: "Payments",
  1007000010749: "Resolutions",
  1007000022040: "SEG",
  1007000024013: "Specialist - Change of Tenancy",
  1007000023316: "Specialist- Bill Accuracy",
  1007000022306: "Specialist- Bill Generation",
  1007000022307: "Specialist- Disputed Reads",
  1007000022308: "Specialist- Erroneous Transfers",
  1007000023205: "Specialist- Industry Corrections",
  1007000024132: "Specialist- Meter Read Triage",
  1007000022788: "Specialist- Metering",
  1007000022309: "Specialist- Registrations",
  1007000019979: "Specialist- Settlements",
  1007000022557: "Test",
  1007000023657: "Trustpilot Reviews",
  1007000023698: "Typeform Bug or Incident Form Responses",
} as const
